import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./Art2DPages.css";

const pages = {
    character: {
        slug: "game-character-design",
        eyebrow: "Game Character Design",
        title: "Character Design for Game Heroes, NPCs, Enemies & Avatars",
        description: "Oengines Games LLP creates game character design for heroes, NPCs, enemies, avatars, skins, mascots, fantasy characters, casual games, card games, and mobile game worlds.",
        keywords: "game character design, 2D character design, game avatar design, NPC character design, mobile game character art, character concept design",
        layout: "hero",
        heroImage: "assets/img/GameDevelopment/Creative-UI-Character-Design.png",
        stats: ["Heroes", "NPCs", "Skins", "Avatars"],
        accent: "Character Lineup",
        services: [
            ["Playable Heroes", "Main characters, avatars, RPG heroes, casual game mascots, card game characters, and progression-ready hero designs."],
            ["NPCs & Enemies", "Villagers, merchants, enemies, bosses, companions, opponents, and side characters with readable personalities."],
            ["Skins & Variants", "Costume sets, rarity versions, seasonal outfits, colorways, weapon looks, and monetization-ready character variants."],
            ["Production Handoff", "Character sheets, pose references, expression notes, palette direction, and clean files for modeling, animation, or UI use."]
        ],
        process: [
            ["Style Target", "Define genre, audience, camera view, monetization need, and visual style."],
            ["Silhouette Pass", "Explore readable shapes, body language, costume forms, and personality."],
            ["Character Sheet", "Prepare final pose, variants, expressions, colors, and usage notes."],
            ["Game Handoff", "Deliver organized files for UI, animation, 3D modeling, or marketing."]
        ],
        deliverables: ["Character sheet", "Pose variations", "Expression set", "Skin variants", "Color palette", "Weapon/accessory notes", "Layered source files", "Export-ready images"],
        faqs: [
            ["Can you design characters for mobile games?", "Yes. We design readable characters for mobile, PC, web, casual, RPG, card, and multiplayer games."],
            ["Can you create skins and rarity variants?", "Yes. We can create alternate outfits, colors, seasonal looks, rarity levels, and monetization-ready variants."],
            ["Can character design support 3D modeling?", "Yes. We can prepare character sheets, reference views, and notes useful for 3D modeling or animation."]
        ]
    },
    concept: {
        slug: "game-concept-art",
        eyebrow: "Game Concept Art",
        title: "Concept Art for Game Worlds, Characters, Props & Visual Direction",
        description: "We create game concept art for characters, environments, props, weapons, UI mood, scenes, level ideas, fantasy worlds, casual games, and production-ready art direction.",
        keywords: "game concept art, 2D concept art for games, environment concept art, character concept art, game art direction, game visual development",
        layout: "world",
        heroImage: "assets/img/GameDevelopment/Battle-Royale-Game-th.png",
        stats: ["Worlds", "Mood", "Props", "Scenes"],
        accent: "Visual Direction",
        services: [
            ["World Concepts", "Fantasy worlds, maps, cities, arenas, battle zones, casual game locations, and environment mood directions."],
            ["Character & Creature Concepts", "Hero ideas, enemy factions, monsters, animals, bosses, and stylized character direction."],
            ["Prop & Weapon Concepts", "Weapons, vehicles, collectibles, UI items, power-ups, cards, rewards, and economy assets."],
            ["Production Style Guides", "Color mood, shape language, references, art notes, and visual consistency for game teams."]
        ],
        process: [
            ["Brief Decode", "Clarify game genre, audience, references, gameplay needs, and art direction."],
            ["Mood Exploration", "Create color, lighting, shape, and theme explorations."],
            ["Concept Frames", "Develop selected ideas into usable visual direction."],
            ["Production Guide", "Deliver references and notes for 2D, 3D, UI, or animation teams."]
        ],
        deliverables: ["Mood boards", "Scene concepts", "Character concepts", "Prop concepts", "Color keys", "Style notes", "Production references", "Layered artwork"],
        faqs: [
            ["Can concept art define the full game style?", "Yes. We can help define mood, colors, characters, worlds, props, and visual direction."],
            ["Can concept art be used by 3D teams?", "Yes. We prepare concept references that help modeling, animation, UI, and production teams."],
            ["Do you create concepts for casual and fantasy games?", "Yes. We work across casual, fantasy, battle, card, puzzle, racing, and mobile game themes."]
        ]
    },
    illustration: {
        slug: "game-illustrations",
        eyebrow: "Game Illustrations",
        title: "Game Illustrations for Store Art, Banners, Cards & In-Game Moments",
        description: "Oengines creates game illustrations for app stores, banners, loading screens, card art, reward art, events, characters, promotional assets, and live game campaigns.",
        keywords: "game illustration, mobile game illustration, game banner art, game card illustration, app store game art, promotional game artwork",
        layout: "poster",
        heroImage: "assets/img/banner/banner-03.jpg",
        stats: ["Store Art", "Cards", "Banners", "Events"],
        accent: "Marketing Art",
        services: [
            ["Store & Promo Art", "App store images, feature graphics, launch banners, event posters, seasonal campaigns, and paid ad artwork."],
            ["Card & Reward Art", "Card illustrations, reward screens, collectibles, chests, badges, achievements, and monetization visuals."],
            ["Character Scenes", "Hero poses, story moments, victory art, battle compositions, and emotional game moments."],
            ["LiveOps Artwork", "Tournament banners, event themes, limited-time offers, seasonal updates, and community content graphics."]
        ],
        process: [
            ["Campaign Goal", "Define platform, audience, offer, event, theme, and required sizes."],
            ["Composition Sketch", "Plan character focus, focal point, hierarchy, and callout space."],
            ["Color & Polish", "Render the artwork with game-friendly color, lighting, and detail."],
            ["Export Set", "Prepare final sizes and formats for store, UI, marketing, or in-game use."]
        ],
        deliverables: ["Store artwork", "Banner art", "Card art", "Loading screen", "Event posters", "Reward graphics", "Source files", "Export sizes"],
        faqs: [
            ["Can you create app store game artwork?", "Yes. We create store graphics, banners, promotional art, and launch visuals for games."],
            ["Can illustrations support LiveOps?", "Yes. We can create event, tournament, seasonal, offer, and update artwork for live games."],
            ["Can you prepare multiple export sizes?", "Yes. We can prepare artwork for store, web, social, in-game UI, or marketing formats."]
        ]
    },
    sprite: {
        slug: "sprite-animation",
        eyebrow: "Sprite Animation",
        title: "Sprite Animation for 2D Games, Characters, Effects & UI Motion",
        description: "We create sprite animation for 2D game characters, enemies, attacks, effects, UI states, item motion, rewards, casual games, card games, and mobile gameplay.",
        keywords: "sprite animation, 2D game sprite animation, character sprite animation, mobile game sprite animation, sprite sheet animation, game animation frames",
        layout: "arcade",
        heroImage: "assets/img/GameDevelopment/3d-game-sprite1.png",
        stats: ["Idle", "Run", "Attack", "Effects"],
        accent: "Frame Motion",
        services: [
            ["Character Sprites", "Idle, walk, run, jump, attack, hit, death, victory, defeat, emote, and interaction sprite cycles."],
            ["Enemy & Boss Sprites", "Monster motion, enemy attacks, boss phases, reactions, warning poses, and readable gameplay states."],
            ["Item & UI Sprites", "Coins, rewards, chests, buttons, cards, power-ups, icons, and interface motion frames."],
            ["Sprite Sheets", "Organized animation frames, sprite sheets, naming, timing notes, and game implementation support."]
        ],
        process: [
            ["Motion List", "Define animation states, frame count, direction, size, and gameplay timing."],
            ["Key Poses", "Create strong readable poses for each action."],
            ["Frame Polish", "Clean motion, timing, spacing, loops, and impact frames."],
            ["Sheet Export", "Deliver organized frames or sheets with implementation notes."]
        ],
        deliverables: ["Sprite sheets", "PNG frames", "Idle cycles", "Attack frames", "Hit reactions", "VFX frames", "Timing notes", "Export naming"],
        faqs: [
            ["Can you create sprite sheets for mobile games?", "Yes. We create sprite sheets, frame exports, and animation timing notes for mobile and web games."],
            ["Can you animate attacks and effects?", "Yes. We create combat actions, reactions, power-ups, reward effects, and UI motion sprites."],
            ["Can sprites loop cleanly?", "Yes. We plan idle, run, effect, and repeated action loops for smooth gameplay use."]
        ]
    },
    spine: {
        slug: "spine-animation",
        eyebrow: "Spine Animation",
        title: "Spine Animation for 2D Game Characters, UI, Avatars & LiveOps",
        description: "Oengines creates Spine animation for 2D game characters, avatars, UI rewards, lobby characters, cards, store items, LiveOps events, and mobile game motion.",
        keywords: "Spine animation, 2D game Spine animation, game character rig animation, Spine avatar animation, mobile game animation, skeletal 2D animation",
        layout: "neon",
        heroImage: "assets/img/GameDevelopment/Creative-UI-Character-Design.png",
        stats: ["Rigged 2D", "Avatars", "Rewards", "Loops"],
        accent: "Skeletal 2D",
        services: [
            ["Character Spine Animation", "Idle, breathing, reactions, emotes, attacks, lobby poses, avatar motion, and gameplay-ready character loops."],
            ["UI & Reward Motion", "Chest openings, card reveals, buttons, badges, reward screens, store items, and monetization animation."],
            ["LiveOps Event Motion", "Seasonal characters, event banners, limited-time offers, tournament moments, and update graphics."],
            ["Implementation Handoff", "Animation naming, loop notes, export guidance, atlas awareness, and developer-friendly file organization."]
        ],
        process: [
            ["Layer Prep", "Review artwork layers, joints, pivots, separation, and animation scope."],
            ["Rig Setup", "Prepare bones, slots, constraints, and basic controls."],
            ["Animation Pass", "Create loops, reactions, UI motion, or gameplay states."],
            ["Export Review", "Check naming, loops, atlas usage, and developer handoff notes."]
        ],
        deliverables: ["Spine project", "Animation loops", "Avatar motion", "Reward animation", "UI motion", "Export notes", "Atlas guidance", "Implementation notes"],
        faqs: [
            ["Can you create Spine animation for game characters?", "Yes. We create Spine character motion, avatar loops, emotes, reactions, and UI animation."],
            ["Can Spine animation be used for LiveOps?", "Yes. It works well for seasonal characters, banners, rewards, and store motion."],
            ["Can you prepare files for developers?", "Yes. We keep naming, loops, export notes, and atlas planning practical for implementation."]
        ]
    },
    uiassets: {
        slug: "game-ui-assets",
        eyebrow: "Game UI Assets",
        title: "Game UI Assets for HUDs, Menus, Icons, Rewards & Store Screens",
        description: "We design colorful game UI assets for HUDs, menus, buttons, icons, cards, rewards, store screens, tournaments, leaderboards, events, and mobile game interfaces.",
        keywords: "game UI assets, 2D game UI design assets, mobile game icons, game HUD assets, game menu assets, reward UI assets, game store UI",
        layout: "interface",
        heroImage: "assets/img/apps/1.png",
        stats: ["HUD", "Icons", "Menus", "Rewards"],
        accent: "Interface Kit",
        services: [
            ["HUD & Menu Assets", "Health bars, timers, currencies, navigation, settings, profile UI, lobby panels, and gameplay overlays."],
            ["Icons & Inventory", "Item icons, skill icons, store icons, currency icons, badges, cards, achievements, and inventory visuals."],
            ["Reward & Store UI", "Chest screens, offers, bundles, battle pass cards, pricing panels, unlock screens, and monetization UI assets."],
            ["Event UI Kits", "Tournament panels, leaderboard graphics, seasonal banners, event frames, missions, and LiveOps screens."]
        ],
        process: [
            ["UI Style System", "Define game mood, colors, shape language, typography fit, and platform constraints."],
            ["Asset Kit Design", "Create reusable UI pieces, icons, panels, buttons, cards, and state variations."],
            ["Screen Fit Review", "Check readability, hierarchy, touch targets, and responsive mobile use."],
            ["Export Package", "Deliver organized assets, states, naming, and implementation notes."]
        ],
        deliverables: ["HUD assets", "Menu panels", "Button states", "Icon set", "Reward UI", "Store assets", "Event UI", "Export-ready files"],
        faqs: [
            ["Can you create UI assets for mobile games?", "Yes. We create colorful HUD, menu, icon, reward, and store assets for mobile game interfaces."],
            ["Can UI assets support monetization?", "Yes. We design offer cards, store screens, reward reveals, bundles, and battle pass assets."],
            ["Can you prepare export-ready files?", "Yes. We organize naming, sizes, states, and assets for implementation."]
        ]
    }
};

function Art2DPage({ page }) {
    const data = pages[page];
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": data.eyebrow,
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": data.eyebrow,
        "areaServed": "Worldwide",
        "description": data.description
    };

    return (
        <>
            <Helmet>
                <title>{data.title} | Oengines Games LLP</title>
                <meta name="description" content={data.description} />
                <meta name="keywords" content={data.keywords} />
                <link rel="canonical" href={`https://www.oengines.com/${data.slug}`} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className={`art2d-page art2d-${data.layout}`}>
                <section className="art2d-hero">
                    <div className="art2d-hero-pattern" />
                    <div className="container">
                        <div className="art2d-hero-grid">
                            <div className="art2d-hero-copy">
                                <span>{data.eyebrow}</span>
                                <h1>{data.title}</h1>
                                <p>{data.description}</p>
                                <div className="art2d-actions">
                                    <a href="/contact" className="as-btn">Start Art Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="art2d-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="art2d-hero-media">
                                <div className="art2d-media-label">{data.accent}</div>
                                {data.layout === "arcade" ? (
                                    <div className="art2d-sprite-stage" role="img" aria-label="CSS generated running game sprite animation preview">
                                        <div className="art2d-sprite-preview">
                                            <div className="art2d-sprite-shadow" />
                                            <div className="art2d-sprite-runner">
                                                <span className="sprite-head" />
                                                <span className="sprite-hair" />
                                                <span className="sprite-body" />
                                                <span className="sprite-arm sprite-arm-left" />
                                                <span className="sprite-arm sprite-arm-right" />
                                                <span className="sprite-leg sprite-leg-left" />
                                                <span className="sprite-leg sprite-leg-right" />
                                                <span className="sprite-boot sprite-boot-left" />
                                                <span className="sprite-boot sprite-boot-right" />
                                            </div>
                                        </div>
                                        <div className="art2d-frame-strip" aria-hidden="true">
                                            {["idle", "run", "jump", "attack", "hit", "loop"].map((frame) => (
                                                <span key={frame}><i />{frame}</span>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <img src={data.heroImage} alt={data.title} />
                                )}
                            </div>
                            <div className="art2d-stat-deck">
                                {data.stats.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="art2d-focus-section">
                    <div className="container">
                        <div className="art2d-focus-strip">
                            <span>Game Art Direction</span>
                            <h2>{data.accent}</h2>
                            <p>Colorful 2D art built around gameplay clarity, store appeal, mobile readability, LiveOps updates, and production handoff.</p>
                        </div>
                    </div>
                </section>

                <section className="art2d-services-section space">
                    <div className="container-fluid px-xxl-5">
                        <div className="art2d-heading">
                            <span>2D Art & Animation Services</span>
                            <h2>Creative Game Art That Supports Production, Monetization & Player Experience</h2>
                        </div>
                        <div className="art2d-service-board">
                            {data.services.map(([title, text], index) => (
                                <article key={title} className={index === 0 ? "featured" : ""}>
                                    <strong>{String(index + 1).padStart(2, "0")}</strong>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="art2d-process-section space">
                    <div className="container">
                        <div className="art2d-process-layout">
                            <div className="art2d-process-title">
                                <span>Production Flow</span>
                                <h2>{data.eyebrow} Workflow</h2>
                                <p>Every project follows a practical game-art workflow so ideas can move from visual direction to usable production assets.</p>
                            </div>
                            <div className="art2d-process-list">
                                {data.process.map(([title, text], index) => (
                                    <article key={title}>
                                        <strong>{String(index + 1).padStart(2, "0")}</strong>
                                        <div>
                                            <h3>{title}</h3>
                                            <p>{text}</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="art2d-deliverables-section space">
                    <div className="container">
                        <div className="art2d-deliverables-grid">
                            <div>
                                <span>Deliverables</span>
                                <h2>Assets Ready for Game Teams</h2>
                                <p>We organize files, exports, states, layers, references, and notes so developers, animators, UI teams, and marketing teams can use the artwork properly.</p>
                            </div>
                            <div className="art2d-deliverables-list">
                                {data.deliverables.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="art2d-faq-section space">
                    <div className="container">
                        <div className="art2d-faq-grid">
                            <div className="art2d-faq-title">
                                <span>FAQ</span>
                                <h2>{data.eyebrow} Questions</h2>
                                <a href="/contact" className="as-btn">Book Art Discussion <i className="fa-regular fa-arrow-right ms-2"></i></a>
                            </div>
                            <div className="art2d-faq-list">
                                {data.faqs.map(([question, answer]) => (
                                    <article key={question}>
                                        <h3>{question}</h3>
                                        <p>{answer}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

export const CharacterDesign = () => <Art2DPage page="character" />;
export const ConceptArt = () => <Art2DPage page="concept" />;
export const GameIllustrations = () => <Art2DPage page="illustration" />;
export const SpriteAnimation = () => <Art2DPage page="sprite" />;
export const SpineAnimation = () => <Art2DPage page="spine" />;
export const GameUiAssets = () => <Art2DPage page="uiassets" />;
